import type {
  IEditGameContentRef,
  IEditableGameContent,
} from '@/pages/common/gameContent/EditGameContent';

import { useRef, useState, useEffect } from 'react';

import { getGameDetails, getGameDropdown } from '@/service/gameService';
import { IGameDetails, IGameDropdownItem } from '@/types/lesson';

type TGameStep = 'form' | 'edit-game';

interface IUseGameContentReturn {
  step: TGameStep;
  setStep: (step: TGameStep) => void;
  editingGame: IEditableGameContent | null;
  editGameRef: React.RefObject<IEditGameContentRef | null>;
  localGameData: IEditableGameContent[];
  setLocalGameData: React.Dispatch<
    React.SetStateAction<IEditableGameContent[]>
  >;
  isGamesLoading: boolean;
  handleGameEditClick: (game: IEditableGameContent) => Promise<void>;
  handleGameEditSave: () => void;
  handleGameEditCancel: () => void;
  resetGameContent: () => void;
}

export function useGameContent(
  isOpen: boolean,
  customFetchGames?: () => Promise<IEditableGameContent[]>
): IUseGameContentReturn {
  const [step, setStep] = useState<TGameStep>('form');
  const [editingGame, setEditingGame] = useState<IEditableGameContent | null>(
    null
  );
  const editGameRef = useRef<IEditGameContentRef | null>(null);
  const [localGameData, setLocalGameData] = useState<IEditableGameContent[]>(
    []
  );
  const [isGamesLoading, setIsGamesLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchGames = async () => {
      setIsGamesLoading(true);
      try {
        if (customFetchGames) {
          const games = await customFetchGames();
          setLocalGameData(games);
        } else {
          const res = await getGameDropdown();
          if (res?.data) {
            const games: IEditableGameContent[] = (
              res.data as IGameDropdownItem[]
            ).map((item) => ({
              id: item.id,
              name: item.title,
              description: item.description,
              is_customizable: item.is_customizable,
              isUpdated: false,
              contentPairs: [],
            }));
            setLocalGameData(games);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsGamesLoading(false);
      }
    };

    fetchGames();
  }, [isOpen, customFetchGames]);

  const handleGameEditClick = async (game: IEditableGameContent) => {
    try {
      const res = await getGameDetails(game.id);
      const details: IGameDetails = res?.data ?? res;
      const existingPairs = game.contentPairs;
      const currentContent: string[] = details.content ?? [];
      const editable: string[] = details.editable_content ?? [];
      const mergedOptions = [...new Set([...currentContent, ...editable])];

      const contentPairs = currentContent.map((word: string, idx: number) => ({
        original: word,
        changeTo: existingPairs[idx]?.changeTo || word,
        options: mergedOptions,
      }));
      setEditingGame({
        ...game,
        name: details.title,
        description: details.description,
        is_customizable: details.is_customizable,
        contentPairs,
      });
    } catch (error) {
      console.error(error);
      setEditingGame({ ...game });
    }
    setStep('edit-game');
  };

  const handleGameEditSave = () => {
    if (!editingGame || !editGameRef.current) return;
    const rawPairs = editGameRef.current.getContentPairs();

    // Fall back to original when changeTo is left empty
    const contentPairs = rawPairs.map((p) => ({
      original: p.original,
      changeTo: p.changeTo.trim() !== '' ? p.changeTo : p.original,
    }));

    // Only mark as updated when at least one value genuinely changed
    const isUpdated = contentPairs.some((p) => p.changeTo !== p.original);

    setLocalGameData((prev) =>
      prev.map((g) =>
        g.id === editingGame.id
          ? { ...editingGame, contentPairs, isUpdated }
          : g
      )
    );
    setStep('form');
    setEditingGame(null);
  };

  const handleGameEditCancel = () => {
    setStep('form');
    setEditingGame(null);
  };

  const resetGameContent = () => {
    setStep('form');
    setEditingGame(null);
    setLocalGameData([]);
  };

  return {
    step,
    setStep,
    editingGame,
    editGameRef,
    localGameData,
    setLocalGameData,
    isGamesLoading,
    handleGameEditClick,
    handleGameEditSave,
    handleGameEditCancel,
    resetGameContent,
  };
}
